import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, Check, UserSearch, Loader2 } from "lucide-react";

export const ClientSearchSelect = ({ value, onSelect, placeholder = "Search clients by name, phone, email…", testid = "client-search-select", currentLabel = null }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selectedLabel, setSelectedLabel] = useState(null);
  const shown = selectedLabel || currentLabel;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: clients = [], isFetching } = useQuery({
    queryKey: ["client-search-select", debounced],
    queryFn: async () => (await api.get(`/clients${debounced ? `?search=${encodeURIComponent(debounced)}` : ""}`)).data,
    enabled: open,
    placeholderData: (prev) => prev,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full h-10 rounded-sm justify-between font-normal text-left"
        >
          <span className="flex items-center gap-2 min-w-0">
            <UserSearch size={14} className="text-cyan-600 shrink-0" />
            <span className={`truncate ${shown ? "text-slate-900" : "text-slate-400"}`}>
              {shown || placeholder}
            </span>
          </span>
          <ChevronsUpDown size={14} className="text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] rounded-sm" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type to search…"
            value={search}
            onValueChange={setSearch}
            data-testid={`${testid}-input`}
          />
          <CommandList className="max-h-64">
            {isFetching && (
              <div className="py-4 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Searching…
              </div>
            )}
            {!isFetching && <CommandEmpty className="py-4 text-center text-xs text-slate-400">No clients found.</CommandEmpty>}
            {clients.map((c) => {
              const pc = c.primary_contact;
              return (
                <CommandItem
                  key={c.id}
                  value={c.id}
                  data-testid={`${testid}-option-${c.id}`}
                  onSelect={() => {
                    setSelectedLabel(c.name);
                    setOpen(false);
                    onSelect(c);
                  }}
                  className="px-3 py-2 cursor-pointer"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {[pc && `${pc.first_name} ${pc.last_name}`, pc?.phone, pc?.email].filter(Boolean).join(" · ") || c.customer_number}
                    </div>
                  </div>
                  {value === c.id && <Check size={14} className="text-cyan-600 shrink-0 ml-2" />}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ClientSearchSelect;
